import moment from 'moment';
import { flow } from 'mobx';
import {
    State,
    getSocketURL,
    getActivePlatform,
    getPropertyValue,
    routes,
    getActionFromUrl,
    checkServerMaintenance,
    useNewWs,
    getAuthSocket,
    getAuthSocketFor,
    getPublicSocket,
    translateActiveSymbolsResponse,
    isLegacyApiAppId,
    addComma,
    getDecimalPlaces,
} from '@deriv/shared';
import { localize } from '@deriv/translations';
import ServerTime from '_common/base/server_time';
import BinarySocket from '_common/base/socket_base';
import WS from './ws-methods';

let client_store, common_store, gtm_store, portfolio_store;
let reconnectionCounter = 1;

// TODO: update commented statements to the corresponding functions from app
const BinarySocketGeneral = (() => {
    let session_duration_limit, session_start_time, session_timeout;

    let responseTimeoutErrorTimer = null;

    const onDisconnect = () => {
        clearTimeout(responseTimeoutErrorTimer);
        common_store.setIsSocketOpened(false);
    };

    const onOpen = is_ready => {
        responseTimeoutErrorTimer = setTimeout(() => {
            const expectedResponseTypes = WS?.get?.()?.expect_response_types || {};
            const pendingResponseTypes = Object.keys(expectedResponseTypes).filter(
                key => expectedResponseTypes[key].state === 'pending'
            );

            const error = new Error('deriv-api: no message received after 30s');
            error.userId = client_store?.loginid;

            window.TrackJS?.console?.error({
                message: error.message,
                clientsCountry: client_store?.clients_country,
                websocketUrl: getSocketURL(),
                pendingResponseTypes,
            });
        }, 30000);

        if (is_ready) {
            if (!client_store.is_valid_login) {
                client_store.logout();
                return;
            }

            if (client_store.is_logged_in || client_store.is_logging_in) {
                WS.get()
                    .expectResponse('authorize')
                    .then(() => {
                        WS.subscribeWebsiteStatus(ResponseHandlers.websiteStatus);
                    });
            } else {
                WS.subscribeWebsiteStatus(ResponseHandlers.websiteStatus);
            }

            ServerTime.init(() => common_store.setServerTime(ServerTime.get()));
            common_store.setIsSocketOpened(true);
        }
    };

    const onMessage = response => {
        clearTimeout(responseTimeoutErrorTimer);
        handleError(response);
        // Header.hideNotification('CONNECTION_ERROR');
        switch (response.msg_type) {
            case 'authorize':
                if (response.error) {
                    const is_active_tab = sessionStorage.getItem('active_tab') === '1';
                    if (getPropertyValue(response, ['error', 'code']) === 'SelfExclusion' && is_active_tab) {
                        sessionStorage.removeItem('active_tab');
                    }
                    client_store.logout();
                } else if (!/authorize/.test(State.get('skip_response'))) {
                    // is_populating_account_list is a check to avoid logout on the first logged-in session
                    // In any other case, if the response loginid does not match the store's loginid, user must be logged out
                    if (
                        response.authorize.loginid !== client_store.loginid &&
                        !client_store.is_populating_account_list &&
                        !client_store.is_switching
                    ) {
                        client_store.logout();
                    } else if (response.authorize.loginid === client_store.loginid) {
                        // All other cases continue with the loginid and authorize the profile
                        authorizeAccount(response);
                    }
                }
                break;
            case 'landing_company':
                // Header.upgradeMessageVisibility();
                break;
            case 'get_self_exclusion':
                setSessionDurationLimit(response);
                break;
            case 'get_settings':
                if (response.get_settings) {
                    setResidence(response.get_settings.country_code);
                    client_store.setEmail(response.get_settings.email);
                    client_store.setAccountSettings(response.get_settings);
                    gtm_store.eventHandler(response.get_settings);
                }
                break;
            case 'set_account_currency':
                WS.forgetAll('balance').then(subscribeBalances);
                break;
            case 'get_account_status':
                client_store.setAccountStatus(response.get_account_status);
                break;
            case 'payout_currencies':
                client_store.responsePayoutCurrencies(response?.payout_currencies);
                break;
            case 'transaction':
                gtm_store.pushTransactionData(response);
                break;
            // no default
        }
    };

    const setResidence = residence => {
        if (residence) {
            client_store.setResidence(residence);
            WS.landingCompany(residence).then(client_store.responseLandingCompany);
        }
    };

    const setSessionDurationLimit = user_limits => {
        const duration = user_limits?.get_self_exclusion?.session_duration_limit;

        session_start_time = new Date(sessionStorage.getItem('session_start_time') || ServerTime.get());
        sessionStorage.setItem('session_start_time', session_start_time);

        if (duration && duration !== session_duration_limit) {
            const current_session_duration = session_duration_limit ? ServerTime.get() - moment(session_start_time) : 0;
            const remaining_session_time = duration * 60 * 1000 - current_session_duration;
            clearTimeout(session_timeout);
            session_timeout = setTimeout(() => {
                client_store.logout();
                sessionStorage.removeItem('session_start_time');
            }, remaining_session_time);
        } else if (!duration) {
            clearTimeout(session_timeout);
        }

        session_duration_limit = duration;
    };

    const setBalanceActiveAccount = flow(function* (obj_balance) {
        // PR-A.1.7: when the legacy WS is gated for new portal app_ids,
        // `BinarySocket.wait('website_status')` returns the never-resolving
        // sentinel from PR-A.1.1's `proxyForAuthorize`, blocking this flow
        // forever. The balance message arrives at the WS but `client_store`
        // is never updated, so the header balance widget stays empty.
        // Skip the wait for new app_ids — `website_status` only matters for
        // the legacy bootstrap that displays maintenance banners.
        if (isLegacyApiAppId()) {
            yield BinarySocket.wait('website_status');
        }
        client_store.setBalanceActiveAccount(obj_balance);
    });

    const setBalanceOtherAccounts = obj_balance => {
        client_store.setBalanceOtherAccounts(obj_balance);
    };

    const handleError = response => {
        const msg_type = response.msg_type;
        const error_code = getPropertyValue(response, ['error', 'code']);
        switch (error_code) {
            case 'WrongResponse':
                // TODO: Remove condition checks below for WrongResponse once mt5 is more reliable
                if (msg_type === 'mt5_login_list') {
                    WS.authorized.mt5LoginList().then(mt5_list_response => {
                        if (!mt5_list_response.error) {
                            client_store.responseMt5LoginList(mt5_list_response);
                            WS.balanceAll().then(balance_response => {
                                if (!balance_response.error)
                                    client_store.setBalanceOtherAccounts(balance_response.balance);
                            });
                        } else {
                            client_store.resetMt5ListPopulatedState();
                        }
                    });
                } else if (msg_type === 'balance') {
                    WS.forgetAll('balance').then(subscribeBalances);
                } else if (msg_type === 'get_account_status') {
                    WS.authorized.getAccountStatus().then(account_status_response => {
                        if (!account_status_response.error) {
                            client_store.setAccountStatus(account_status_response.get_account_status);
                        }
                    });
                } else if (msg_type === 'landing_company') {
                    if (client_store.residence) {
                        WS.authorized.landingCompany(client_store.residence).then(landing_company_response => {
                            if (!landing_company_response.error) {
                                client_store.responseLandingCompany(landing_company_response);
                            }
                        });
                    }
                }
                break;
            case 'InternalServerError':
            case 'OutputValidationFailed': {
                if (msg_type !== 'mt5_login_list') {
                    common_store.setError(true, { message: response.error.message });
                }
                break;
            }
            case 'RateLimit':
                if (msg_type !== 'cashier_password') {
                    common_store.setError(true, {
                        message: localize('You have reached the rate limit of requests per second. Please try later.'),
                    });
                }
                break;
            case 'InvalidAppID':
                common_store.setError(true, { message: response.error.message });
                break;
            case 'DisabledClient':
                common_store.setError(true, { message: response.error.message });
                break;
            case 'InvalidToken':
                if (
                    [
                        'cashier',
                        'paymentagent_withdraw',
                        'reset_password',
                        'trading_platform_password_reset',
                        'trading_platform_investor_password_reset',
                        'new_account_virtual',
                        'portfolio',
                        'proposal_open_contract',
                        'change_email',
                    ].includes(msg_type)
                ) {
                    return;
                }
                // eslint-disable-next-line no-case-declarations
                const active_platform = getActivePlatform(common_store.app_routing_history);

                // DBot handles this internally. Special case: 'client.invalid_token'
                if (active_platform === 'DBot') return;

                client_store.logout().then(() => {
                    let redirect_to = routes.trade;
                    const action = getActionFromUrl();
                    if (action === 'system_email_change') {
                        return;
                    }
                    if (active_platform === 'Deriv MT5') {
                        redirect_to = routes.mt5;
                    }
                    common_store.routeTo(redirect_to);
                });
                break;
            case 'AuthorizationRequired':
                // if msg_type is coming from 'buy', behaviour should be handled in app itself.
                if (msg_type === 'buy') {
                    return;
                }
                client_store.logout();
                break;
            // no default
        }
    };

    const init = store => {
        client_store = store.client;
        common_store = store.common;
        gtm_store = store.gtm;
        // PR-A.2.13: the native @deriv/trader shares this exact portfolio store
        // (RootStore.portfolio === core.portfolio). We push manual buys into it
        // directly (see newWsBuy) because the new /options/ws endpoint doesn't
        // emit the account-wide transaction / proposal_open_contract streams the
        // portfolio normally populates from.
        portfolio_store = store.portfolio;

        return {
            onDisconnect,
            onOpen,
            onMessage,
        };
    };

    let _new_ws_balance_subscription = null;

    // PR-B.2.2: per-loginid balance subscriptions for non-active accounts.
    // The active account's subscription stays in `_new_ws_balance_subscription`
    // (PR-B.2.1). Cap at 3 concurrent siblings — Deriv's limit is 5 WS/user
    // (gross), public socket from PR-B.1 uses 1 slot, active-account auth
    // socket from PR-B.2.1 uses another, leaves 3 for siblings.
    const MAX_OTHER_ACCOUNT_SUBS = 3;
    const _new_ws_other_balance_subscriptions = new Map();

    const subscribeMultiAccountBalances = async () => {
        if (!useNewWs()) return;

        let raw;
        try {
            raw = JSON.parse(localStorage.getItem('client.accounts') || '{}');
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[balance:all] client.accounts is not valid JSON; aborting');
            return;
        }
        const all_loginids = Object.keys(raw);
        const active = localStorage.getItem('active_loginid');
        const sibling_loginids = all_loginids
            .filter(id => id !== active && raw[id]?.token)
            .slice(0, MAX_OTHER_ACCOUNT_SUBS);

        // Cancel any prior subscriptions before re-opening — same pattern as
        // `_new_ws_balance_subscription` in `subscribeBalances`.
        for (const [, sub] of _new_ws_other_balance_subscriptions) sub.unsubscribe();
        _new_ws_other_balance_subscriptions.clear();

        for (const loginid of sibling_loginids) {
            try {
                const socket = await getAuthSocketFor(loginid);
                const sub = socket.subscribe({ balance: 1 }, msg => {
                    if (msg?.error) {
                        // eslint-disable-next-line no-console
                        console.warn(`[balance:all] ${loginid} subscription error:`, msg.error);
                        return;
                    }
                    if (!msg?.balance) return;
                    // setBalanceOtherAccounts expects the inner balance object
                    // (reads obj_balance.loginid and obj_balance.balance directly
                    // — see client-store.js setBalanceOtherAccounts). Pass
                    // msg.balance, NOT the full envelope — different contract from
                    // setBalanceActiveAccount (which takes the envelope).
                    client_store.setBalanceOtherAccounts(msg.balance);
                });
                _new_ws_other_balance_subscriptions.set(loginid, sub);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn(`[balance:all] failed to open auth socket for ${loginid}:`, err?.message ?? String(err));
            }
        }
    };

    const subscribeBalances = () => {
        if (useNewWs()) {
            // PR-B.2.1: route the active-account balance subscription through the
            // new auth WS. balance:all (multi-account total) stays on the legacy
            // path for now — it'll move in a follow-up PR-B.2.x. With the gate on,
            // legacy WS is gated entirely (PR-A.1), so the legacy branch is a
            // no-op. We swallow factory errors — the header balance widget shows
            // blank rather than crashing the app.
            getAuthSocket()
                .then(socket => {
                    // Cancel any prior subscription before replacing it. The 3
                    // call sites (authorizeAccount, set_account_currency,
                    // balance-WrongResponse) can fire repeatedly during one
                    // session — without this, each fires a duplicate subscribe
                    // and the store's setBalanceActiveAccount runs N times
                    // per tick.
                    if (_new_ws_balance_subscription) {
                        _new_ws_balance_subscription.unsubscribe();
                    }
                    _new_ws_balance_subscription = socket.subscribe({ balance: 1 }, msg => {
                        // ResponseHandlers.balanceActiveAccount expects the full
                        // message envelope (it reads `.error` and `.balance`
                        // itself — see balanceActiveAccount definition around
                        // line 345 of this file). Pass `msg` unwrapped,
                        // NOT `msg.balance`.
                        ResponseHandlers.balanceActiveAccount(msg);
                    });
                })
                .catch(err => {
                    // eslint-disable-next-line no-console
                    console.warn('[balance] auth-socket factory failed:', err?.message ?? String(err));
                });
            return;
        }

        WS.subscribeBalanceAll(ResponseHandlers.balanceOtherAccounts);
        WS.subscribeBalanceActiveAccount(ResponseHandlers.balanceActiveAccount, client_store.loginid);
    };

    // PR-B.2.3: one-shot portfolio fetch on the active-account auth WS.
    // The new WS rejects `subscribe: 1` on portfolio (verified via Task 1
    // schema probe — `InputValidationFailed: Properties not allowed: subscribe`)
    // so this uses socket.send() not socket.subscribe(). Returns a Promise
    // resolving to a legacy-shaped envelope so callers can chain `.then`
    // exactly as they did with the legacy `WS.portfolio()`.
    //
    // Position state updates flow via subscribeProposalOpenContract +
    // subscribeTransaction (PR-B.2.6 + PR-B.2.7 territory, still legacy WS).
    const fetchPortfolio = async () => {
        let socket;
        try {
            socket = await getAuthSocket();
        } catch (err) {
            return { error: { message: `fetchPortfolio: auth socket open failed: ${err?.message ?? String(err)}` } };
        }
        try {
            const response = await socket.send({ portfolio: 1 });
            // Defensive — current NewBinarySocket.send() rejects on server
            // error instead of resolving with {error}, so this branch is
            // belt-and-suspenders against future infra changes.
            if (response?.error) return { error: response.error };
            if (!response?.portfolio) {
                return { error: { message: 'fetchPortfolio: response missing portfolio field' } };
            }
            // Pass through unchanged — Task 1 probe confirmed the envelope
            // matches the legacy shape exactly: { portfolio: { contracts: [] },
            // msg_type: 'portfolio', req_id: ... }
            return { portfolio: response.portfolio, msg_type: 'portfolio' };
        } catch (err) {
            // NewBinarySocket.send() rejects with the raw server error object
            // (`{ code, message, ... }`) when the response carries `.error`.
            // Preserve that structured shape so portfolioHandler can surface
            // `error.code` if it wants to. Fall back to a wrapped string for
            // genuinely unstructured throws.
            if (err && typeof err.code === 'string' && typeof err.message === 'string') {
                return { error: err };
            }
            return { error: { message: `fetchPortfolio: send failed: ${err?.message ?? String(err)}` } };
        }
    };

    // PR-CU.1: contract_update / contract_update_history / cancel on the
    // active-account auth WS. On new-portal app_ids the legacy socket is dead, so
    // socket_base's `deriv_api.send(...)` versions hang. Request shapes are
    // byte-identical to the legacy payloads (verified against the new-API docs —
    // contract_update/contract_update_history/cancel are all in the new WS
    // surface), so these just carry the same payloads on getAuthSocket(). Each
    // returns a legacy-shaped envelope (incl. msg_type on both success + error)
    // so the trader/replay consumers chain `.then` unchanged.
    const newWsContractUpdate = async (contract_id, limit_order) => {
        let socket;
        try {
            socket = await getAuthSocket();
        } catch (err) {
            return {
                error: { message: `contractUpdate: auth socket open failed: ${err?.message ?? String(err)}` },
                msg_type: 'contract_update',
            };
        }
        try {
            const response = await socket.send({ contract_update: 1, contract_id, limit_order });
            if (response?.error) return { error: response.error, msg_type: 'contract_update' };
            return { contract_update: response.contract_update, msg_type: 'contract_update' };
        } catch (err) {
            if (err && typeof err.code === 'string' && typeof err.message === 'string') {
                return { error: err, msg_type: 'contract_update' };
            }
            return {
                error: { message: `contractUpdate: send failed: ${err?.message ?? String(err)}` },
                msg_type: 'contract_update',
            };
        }
    };

    const newWsContractUpdateHistory = async contract_id => {
        let socket;
        try {
            socket = await getAuthSocket();
        } catch (err) {
            return {
                error: { message: `contractUpdateHistory: auth socket open failed: ${err?.message ?? String(err)}` },
                msg_type: 'contract_update_history',
            };
        }
        try {
            const response = await socket.send({ contract_update_history: 1, contract_id });
            if (response?.error) return { error: response.error, msg_type: 'contract_update_history' };
            return {
                contract_update_history: response.contract_update_history,
                msg_type: 'contract_update_history',
            };
        } catch (err) {
            if (err && typeof err.code === 'string' && typeof err.message === 'string') {
                return { error: err, msg_type: 'contract_update_history' };
            }
            return {
                error: { message: `contractUpdateHistory: send failed: ${err?.message ?? String(err)}` },
                msg_type: 'contract_update_history',
            };
        }
    };

    const newWsCancelContract = async contract_id => {
        let socket;
        try {
            socket = await getAuthSocket();
        } catch (err) {
            return {
                error: { message: `cancelContract: auth socket open failed: ${err?.message ?? String(err)}` },
                msg_type: 'cancel',
            };
        }
        try {
            const response = await socket.send({ cancel: contract_id });
            if (response?.error) return { error: response.error, msg_type: 'cancel' };
            return { cancel: response.cancel, msg_type: 'cancel' };
        } catch (err) {
            if (err && typeof err.code === 'string' && typeof err.message === 'string') {
                return { error: err, msg_type: 'cancel' };
            }
            return {
                error: { message: `cancelContract: send failed: ${err?.message ?? String(err)}` },
                msg_type: 'cancel',
            };
        }
    };

    // PR-B.2.4: per-signature proposal subscriptions on the active-account
    // auth WS. Map key is a deterministic signature of the request
    // (contract_type / underlying / duration / amount / basis / currency)
    // so re-subscribing for the same combo automatically cancels the prior.
    // Cleared via forgetAllProposals() when the trader view unmounts or
    // validation errors trigger a reset.
    const _new_ws_proposal_subscriptions = new Map();

    // Schema translation for the new auth WS. Task 1 probe (2026-05-12)
    // confirmed:
    //   • `symbol: 'R_100'` (legacy field) → rejected with
    //     `Properties not allowed: symbol`
    //   • `underlying_symbol: '1HZ100V'` (new field, new value) → success
    //   • `underlying_symbol: 'R_100'` (new field, legacy value) → success
    // So we ONLY rename the field; the API resolves both legacy and new
    // symbol values internally. No value-translation table needed.
    // PR-MT.3 (2026-06-23) — fix the BOT's Higher/Lower so it prices + buys on
    // the new Deriv options WS. Root cause is identical to the manual-panel fix
    // (PR-MT.2.3 / PR #259): the new options WS treats CALL/PUT as ATM-ONLY. A
    // barriered up/down tick contract is its OWN contract type there —
    //   HIGHER (up) / LOWER (down)   in contract_category 'higherlower'.
    // Sending legacy CALL/PUT + a price-offset `barrier` is rejected with
    //   ContractBuyValidationError: Invalid barrier
    // (live-probe proven 2026-06-22 on R_100: HIGHER + "+0.18" @5t → payout
    // 5.17; CALL + ANY barrier → "Invalid barrier"; an UNSIGNED "0.18" or
    // ABSOLUTE "351.25" barrier on HIGHER also errors — the barrier MUST be a
    // SIGNED RELATIVE offset). The bot maps the HIGHERLOWER strategy to
    // CALL/PUT in bot-skeleton/src/constants/config.js (lines 164-170) and
    // helpers.js (tradeOptionToProposal / tradeOptionToBuy) attaches the user's
    // barrierOffset, so every Higher/Lower bot proposal/buy fails the same way.
    //
    // We fix it at the OUTGOING wire boundary ONLY (this translation layer),
    // touching nothing else:
    //   • IF contract_type === 'CALL' AND a meaningful barrier is present →
    //     wire contract_type = 'HIGHER'.
    //   • IF contract_type === 'PUT'  AND a meaningful barrier is present →
    //     wire contract_type = 'LOWER'.
    //   • The barrier is normalized to a SIGNED RELATIVE offset (HIGHER → '+',
    //     LOWER → '−') because the new WS rejects unsigned/absolute forms.
    //
    // CRITICAL GATING — the safety boundary (the #1 rule: never affect any
    // non-Higher/Lower contract):
    //   • "Meaningful barrier present" = a non-empty, non-zero relative offset.
    //     Rise/Fall (config.js CALLPUT → CALL/PUT) sends CALL/PUT with NO
    //     barrier (helpers.js only sets `barrier` when prediction or
    //     barrierOffset is defined — neither is for Rise/Fall), so it is NEVER
    //     remapped and stays ATM CALL/PUT.
    //   • DIGIT contracts (DIGITEVEN/ODD/OVER/UNDER/MATCH/DIFF), Touch/No-Touch
    //     (ONETOUCH/NOTOUCH), Asians, etc. are not CALL/PUT → never touched.
    //   • passthrough is NOT altered. The bot correlates proposal responses via
    //     passthrough.contract_type (which stays CALL/PUT) and buys by proposal
    //     id (Proposal.js / Purchase.js); the HIGHER proposal id redeems fine.
    //     We only rewrite the wire-level contract_type, never the passthrough.
    //
    // NOTE (documented, NOT auto-fixed): HIGHER/LOWER require 5–10 tick
    // durations on the new WS. A Higher/Lower bot configured with <5 ticks will
    // still be rejected — that's the user's strategy config; we do NOT silently
    // clamp it (that would change trade behavior).
    const _isMeaningfulHigherLowerBarrier = barrier => {
        if (barrier === undefined || barrier === null) return false;
        const str = String(barrier).trim();
        if (str === '' || str === '0') return false;
        // A barrier of literal "0", "+0", "-0", "0.0" etc. is an ATM contract,
        // not a Higher/Lower — treat as no barrier so CALL/PUT stays unchanged.
        const numeric = Number(str.replace(/^\+/, ''));
        if (Number.isFinite(numeric) && numeric === 0) return false;
        return true;
    };

    // Normalize a Higher/Lower barrier to a SIGNED RELATIVE offset string.
    // up=true → '+<mag>' (HIGHER, above spot); up=false → '-<mag>' (LOWER,
    // below spot). Already-signed offsets ('+0.18' / '-0.18') keep their
    // magnitude; the sign is forced from the direction so it can never disagree
    // with the contract type. Unsigned ('0.18') or absolute barriers are
    // converted to the required signed-relative form.
    const _toSignedRelativeBarrier = (barrier, up) => {
        const mag = Math.abs(Number(String(barrier).trim().replace(/^[+-]/, '')));
        if (!Number.isFinite(mag)) return barrier; // leave non-numeric barriers untouched
        return `${up ? '+' : '-'}${mag}`;
    };

    // Apply the gated CALL→HIGHER / PUT→LOWER remap to a wire payload that
    // carries `contract_type` + (optionally) `barrier`. Returns a NEW object
    // when a remap applies; otherwise returns the input untouched. Operates on
    // the wire fields only — callers must NOT pass `passthrough` through here.
    const remapHigherLowerContractType = payload => {
        if (!payload || typeof payload !== 'object') return payload;
        const { contract_type, barrier } = payload;
        if (contract_type !== 'CALL' && contract_type !== 'PUT') return payload;
        if (!_isMeaningfulHigherLowerBarrier(barrier)) return payload;
        const up = contract_type === 'CALL';
        return {
            ...payload,
            contract_type: up ? 'HIGHER' : 'LOWER',
            barrier: _toSignedRelativeBarrier(barrier, up),
        };
    };

    const translateProposalRequest = request => {
        if (!request || typeof request !== 'object') return request;
        // PR-MT.3: gated CALL/PUT+barrier → HIGHER/LOWER remap of the OUTGOING
        // wire contract_type. `passthrough` (which the bot correlates on) is
        // preserved verbatim — only the top-level wire contract_type/barrier is
        // touched, and only for barriered CALL/PUT (Higher/Lower).
        const remapped = remapHigherLowerContractType(request);
        const { symbol, ...rest } = remapped;
        // PR-B.2.4.1: legacy `WS.subscribeProposal(req, cb)` wraps payloads
        // with `{proposal: 1, ...req}` inside socket_base.js's `subscribe()`
        // call. The new-WS routing bypasses that wrap, so callers' payloads
        // arrived at the server WITHOUT `proposal: 1` and were rejected
        // with `"Unrecognised request"`. Re-introduce the operation key
        // here at the service boundary so callers stay transport-agnostic.
        const withOp = { proposal: 1, ...rest };
        if (symbol === undefined) return withOp;
        return { ...withOp, underlying_symbol: symbol };
    };

    const proposalSignature = request =>
        JSON.stringify({
            contract_type: request?.contract_type,
            symbol: request?.symbol,
            duration: request?.duration,
            duration_unit: request?.duration_unit,
            amount: request?.amount,
            basis: request?.basis,
            currency: request?.currency,
        });

    // PR-A.1.9 — complete the proposal RESPONSE shape the @deriv/trader reads.
    //
    // The new options WS proposal response (deriv_api_doc, /proposal) returns:
    //   { proposal: { id, ask_price, payout, spot, spot_time, date_start,
    //                 longcode }, msg_type:'proposal' }
    // The trader's getProposalInfo (Helpers/proposal.ts:57-108) reads:
    //   • payout / ask_price / id / spot / longcode  → ALL present ✓
    //   • display_value (proposal.display_value)     → ABSENT on new WS ✗
    // `display_value` is the legacy formatted stake/ask_price string the
    // Purchase fieldset shows via <Money amount={stake}/> (contract-info.tsx:114,
    // `const { stake } = proposal_info; stake = proposal.display_value`). Without
    // it the Buy button still enables (it gates on `info.id`, present) but the
    // stake price renders blank. Synthesize it from `ask_price` formatted to the
    // request currency's decimal places — exactly what the legacy backend put in
    // `display_value`. ADDITIVE: a server-provided `display_value` always wins;
    // we only fill the gap. Touches nothing the bot reads (the bot ignores
    // display_value — it buys by id/parameters and reads payout/ask_price).
    const fillProposalDisplayFields = (msg, request) => {
        const proposal = msg?.proposal;
        if (!proposal || typeof proposal !== 'object') return msg;
        // Only fill when the server genuinely omitted display_value.
        if (proposal.display_value !== undefined && proposal.display_value !== null && proposal.display_value !== '') {
            return msg;
        }
        const ask_price = proposal.ask_price;
        if (ask_price === undefined || ask_price === null || ask_price === '') return msg;
        const currency = request?.currency || '';
        let display_value;
        try {
            display_value = addComma(ask_price, getDecimalPlaces(currency));
        } catch {
            display_value = String(ask_price);
        }
        return { ...msg, proposal: { ...proposal, display_value } };
    };

    // PR-A.2.19 — open the auth socket, retrying while the account token is not
    // yet in localStorage (the cold-reload race that left purchase buttons greyed
    // until a symbol reselect). ~5s budget of escalating waits; rethrows the last
    // error if the token never appears (genuinely logged out).
    const openAuthSocketWithRetry = async (attempts = 12, delay_ms = 400) => {
        let last_err;
        for (let i = 0; i < attempts; i++) {
            try {
                // eslint-disable-next-line no-await-in-loop
                return await getAuthSocket();
            } catch (err) {
                last_err = err;
                // eslint-disable-next-line no-await-in-loop
                await new Promise(resolve => setTimeout(resolve, delay_ms));
            }
        }
        throw last_err;
    };

    const subscribeProposal = async (request, callback) => {
        if (!useNewWs()) return;

        const sig = proposalSignature(request);
        const existing = _new_ws_proposal_subscriptions.get(sig);
        if (existing) {
            try {
                existing.unsubscribe();
            } catch {
                // ignore — best-effort
            }
            _new_ws_proposal_subscriptions.delete(sig);
        }

        // PR-A.2.28 — LOGGED-OUT PAYOUT PREVIEW + no scary error toast.
        // Without a token the auth socket can't open, so logged-out the old code
        // surfaced a red "active_loginid not in localStorage (user not logged in)"
        // toast and greyed the buttons. Like the competitor, route the proposal
        // through the PUBLIC socket when logged out so a payout preview can render
        // (active_symbols / ticks / contracts_for are already public on the new
        // portal; if `proposal` isn't public it just returns no payout — never a
        // toast). Buying still requires login (the trader prompts it on Buy).
        let socket;
        let suppress_errors = false;
        if (!client_store?.is_logged_in) {
            socket = getConnectedPublicSocket();
            suppress_errors = true; // logged-out preview: never toast a proposal error
        } else {
            try {
                // PR-A.2.19 — on a cold reload the trader fires its FIRST proposal
                // before core's auth bootstrap writes the account token to
                // localStorage; retry the auth-socket open with backoff so the
                // first proposal resolves without a symbol reselect.
                socket = await openAuthSocketWithRetry();
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[proposal] auth socket open failed (transient):', err?.message ?? String(err));
                // PR-A.2.28: do NOT surface a scary error toast. The keepalive +
                // reconnect + the proposal watchdog recover the auth socket and the
                // trader re-requests on the next value change. Greyed buttons with
                // no red error beat a wall of "user not logged in" toasts.
                return;
            }
        }

        // PR-A.2.22 — PROPOSAL WATCHDOG. The auth socket self-heals on RECONNECT
        // (NewBinarySocket.replaySubscriptions re-issues active subscriptions on
        // every onopen), but a proposal that is SENT and then silently never
        // answered — no error frame, no socket close (e.g. issued in a brief
        // pre-auth window) — leaves the purchase buttons greyed with no payout
        // until the user changes a value. PR-A.2.19's getAuthSocket retry only
        // covers a FAILED socket open, not a silent no-response, so the owner
        // still hit intermittent greying (live trace: every FRESH load resolved,
        // but their session occasionally didn't). If no VALID proposal arrives
        // within the timeout, re-issue the subscription (bounded). Read-only (a
        // price quote, never a trade); the sig dedup + unsubscribe keep one live
        // sub, so a re-issue can't double-charge or leak.
        let resolved = false;
        let attempts = 0;
        let watchdog = null;
        const clearWatchdog = () => {
            if (watchdog) {
                clearTimeout(watchdog);
                watchdog = null;
            }
        };

        const onProposalMsg = msg => {
            if (msg?.error) {
                // PR-A.2.28: in logged-out / public-preview mode suppress proposal
                // errors (e.g. the public socket rejecting `proposal`) so no scary
                // toast shows. Otherwise preserve the structured error shape
                // (PR-B.2.3) so onProposalResponse can read echo_req.contract_type.
                if (!suppress_errors) callback({ error: msg.error, echo_req: request });
                return;
            }
            if (!msg?.proposal) return;
            // A valid proposal arrived — the buttons can enable; stop the
            // watchdog so we don't needlessly re-issue.
            resolved = true;
            clearWatchdog();
            // PR-A.1.8: the new options WS proposal response does NOT echo back
            // the request. Its documented envelope is:
            //   { proposal: { id, ask_price, payout, spot, ... },
            //     msg_type: 'proposal', req_id }   — NO `echo_req`.
            // The @deriv/trader correlates each proposal response to its pending
            // trade-type by reading `response.echo_req.contract_type`
            // (trade-store.ts onProposalResponse → `const { contract_type } =
            // response.echo_req`). With `echo_req` absent that destructure
            // either throws or yields `contract_type: undefined`, so the trader
            // writes proposal_info[undefined] and the real contract-type panel
            // never fills → trade-params stay skeleton + payout stuck at 0.00,
            // with NO wire-level error (the proposal itself succeeded).
            //
            // Synthesize a legacy-shaped `echo_req` from the ORIGINAL
            // (untranslated) `request` — the same workaround already applied to
            // the public-data path (translatePublicResponse) and the bot shim
            // (public-ws-shim synthesizeEchoReq). We use the untranslated
            // request on purpose so:
            //   • `contract_type` stays the LEGACY trade-type the trader keyed
            //     its proposal_requests on. For Higher/Lower this is CALL/PUT
            //     (PR-MT.3 only remaps the OUTGOING wire type to HIGHER/LOWER;
            //     the trader/bot still correlate on CALL/PUT — see remap
            //     docblock above), so correlation round-trips correctly.
            //   • `symbol` stays the legacy field name the trader reads in
            //     setMainBarrier(response.echo_req).
            // A server-provided echo_req (should one ever appear) is preserved
            // underneath, but the original request's correlation fields win.
            const with_echo = msg.echo_req ? msg : { ...msg, echo_req: { ...request } };
            // PR-A.1.9: fill display_value (and any future gap) so the trader's
            // getProposalInfo produces a complete proposal_info → the stake price
            // renders and the payout/Buy chain is fully populated.
            const out = fillProposalDisplayFields(with_echo, request);
            callback(out);
        };

        const subscribeOnce = () => {
            const translated = translateProposalRequest(request);
            const sub = socket.subscribe(translated, onProposalMsg);
            // Store a WRAPPER so that an unsubscribe from any source (the trader on
            // a value change, the sig-dedup cleanup above, or forgetAllProposals)
            // also stops the watchdog — otherwise a still-pending watchdog could
            // re-issue a now-stale proposal or outlive the component.
            _new_ws_proposal_subscriptions.set(sig, {
                unsubscribe: () => {
                    resolved = true; // also stops any in-flight watchdog
                    clearWatchdog();
                    try {
                        sub.unsubscribe();
                    } catch {
                        /* ignore */
                    }
                },
            });
            // If no valid proposal arrives in 4s, re-issue (bounded to 3 tries) —
            // this is the self-heal for a silently dropped proposal that would
            // otherwise leave the Buy buttons greyed until a manual value change.
            clearWatchdog();
            watchdog = setTimeout(() => {
                if (resolved || attempts >= 3) return;
                attempts++;
                try {
                    sub.unsubscribe();
                } catch {
                    /* ignore */
                }
                subscribeOnce();
            }, 4000);
        };
        subscribeOnce();
    };

    const forgetAllProposals = () => {
        for (const [, sub] of _new_ws_proposal_subscriptions) {
            try {
                sub.unsubscribe();
            } catch {
                // ignore
            }
        }
        _new_ws_proposal_subscriptions.clear();
    };

    // PR-B.2.5: buy/sell on the new auth WS. The new endpoint accepts
    // byte-identical request shapes to legacy (`{buy, price}` /
    // `{sell, price}`), but the proposal_id is socket-scoped — it MUST be
    // redeemed on the same WebSocket that issued the proposal. Using
    // `getAuthSocket()` (the singleton from PR-B.2.1) for both
    // subscribeProposal and buy guarantees that constraint is met
    // transparently. Response envelopes match legacy:
    //   buy  → { buy: { contract_id, balance_after, ... }, msg_type: 'buy' }
    //   sell → { sell: { contract_id, sold_for, balance_after, ... }, msg_type: 'sell' }
    // PR-RT.2: extended signature to handle the bot's "direct-buy with
    // parameters" path. The bot's `tradeOptionToBuy()` (no-proposal
    // subscription mode, used by Quick Strategy among others) sends:
    //   { buy: '1', subscribe: 1, price, parameters: { amount, basis,
    //     contract_type, currency, duration, duration_unit, symbol, ... } }
    // Previously this function dropped `parameters` and `subscribe`, so the
    // server got `{buy: '1', price}` with no idea what to trade and
    // silently dropped the request. Bot stuck at "Buying contract" forever.
    // Now we forward the full shape.
    //
    // PR-RT.3: same schema-drift as `translateProposalRequest` — the new auth
    // WS rejects `parameters.symbol` and requires `parameters.underlying_symbol`
    // instead. Live-smoke 2026-05-16 captured a complete buy frame
    //   {"buy":"1","price":1,"parameters":{...,"symbol":"1HZ100V"},"req_id":3}
    // that received NO response (not even an error envelope) — the server
    // silently dropped it for the same reason proposals were rejected before
    // PR-B.2.4. Translation reuses the same idiom: lift `symbol` out, replace
    // with `underlying_symbol`. The API resolves both legacy and new symbol
    // values internally, so no value-translation table is needed.
    const translateBuyParameters = params => {
        if (!params || typeof params !== 'object') return params;
        // PR-MT.3: same gated CALL/PUT+barrier → HIGHER/LOWER remap as the
        // proposal path, applied to the parameters-buy shape (the bot's
        // stake-basis direct buy: { buy:'1', price, parameters:{ contract_type,
        // ..., barrier } } — Purchase.js:113-116 / helpers.js tradeOptionToBuy).
        // The buy `parameters` object carries NO `passthrough` (the bot only
        // puts passthrough in the proposal), so this rewrites only the wire
        // contract_type/barrier — Rise/Fall (no barrier) and digit/other
        // contracts are untouched.
        const remapped = remapHigherLowerContractType(params);
        const { symbol, ...rest } = remapped;
        if (symbol === undefined) return rest;
        return { ...rest, underlying_symbol: symbol };
    };

    const newWsBuy = async ({ proposal_id, price, parameters, subscribe }) => {
        let socket;
        try {
            socket = await getAuthSocket();
        } catch (err) {
            return { error: { message: `buy: auth socket open failed: ${err?.message ?? String(err)}` } };
        }
        try {
            const payload = { buy: proposal_id, price };
            if (parameters) payload.parameters = translateBuyParameters(parameters);
            if (subscribe) payload.subscribe = 1;
            const response = await socket.send(payload);
            if (response?.error) return { error: response.error };

            // PR-A.2.13: populate the trader's Open Positions on the NATIVE mount.
            // The new /options/ws endpoint does NOT emit the account-wide
            // `transaction` / `proposal_open_contract` streams the portfolio
            // normally builds positions from, so manual buys never showed in the
            // positions drawer. Here we (1) add the row directly via
            // `onBuyResponse` (pushNewPosition dedups on contract_id), and (2)
            // subscribe the PER-CONTRACT POC (which the endpoint DOES support)
            // routed to the portfolio's existing handler so the row updates live
            // and settles/clears on sell. GATED on the native-trader flag so the
            // BOT (which tracks via `bot.contract`, not portfolio) is untouched;
            // also only for proposal_id buys (the manual trader path), never the
            // bot's parameters-buy.
            try {
                const bought = response?.buy;
                if (
                    typeof window !== 'undefined' &&
                    window.__FX_NATIVE_TRADER_MOUNTED__ === true &&
                    portfolio_store &&
                    bought?.contract_id
                ) {
                    // PR-A.2.13.3 ROOT-CAUSE FIX (diag-confirmed): the new
                    // /options/ws proposal_open_contract frames OMIT the
                    // `underlying` field (the symbol only lives inside `shortcode`,
                    // e.g. DIGITEVEN_1HZ100V_181.82_...). The trader's
                    // PositionsDrawer filters `symbol === contract_info.underlying`
                    // (positions-drawer.tsx:129), so with underlying===undefined
                    // EVERY row was filtered out → "no open positions". Derive the
                    // underlying from the shortcode (handles symbols that contain
                    // '_' like R_100 by collecting tokens after the contract_type
                    // until the decimal payout token) and inject it into each POC
                    // frame so contract_info.underlying matches the drawer filter.
                    const deriveUnderlying = shortcode => {
                        if (typeof shortcode !== 'string') return null;
                        const parts = shortcode.split('_');
                        const tokens = [];
                        for (let i = 1; i < parts.length; i++) {
                            if (/\d\.\d/.test(parts[i])) break; // payout, e.g. 181.82
                            tokens.push(parts[i]);
                        }
                        return tokens.length ? tokens.join('_') : null;
                    };
                    const underlying = deriveUnderlying(bought.shortcode);

                    portfolio_store.onBuyResponse?.({
                        contract_id: bought.contract_id,
                        longcode: bought.longcode,
                        contract_type: parameters?.contract_type ?? '',
                    });
                    // eslint-disable-next-line prefer-const
                    let _journal_poc_sub;
                    _journal_poc_sub = newWsSubscribeProposalOpenContract(bought.contract_id, poc => {
                        try {
                            const c = poc?.proposal_open_contract;
                            if (c && !c.underlying && underlying) {
                                c.underlying = underlying;
                            }
                            portfolio_store.proposalOpenContractQueueHandler?.(poc);

                            // PR-A.2.15 / PR-A.2.18: journal the manual trade on
                            // SETTLE. The native-trader tab registers
                            // `__FX_JOURNAL_MANUAL__` (bound to
                            // journalCompletedContract + the live loginid) — core
                            // can't import @deriv/bot-skeleton, so we hop the
                            // bot-web-ui boundary via this window hook.
                            // PR-A.2.18: the new /options/ws per-contract POC does
                            // NOT reliably set `is_sold` on the frame this callback
                            // sees at settlement (live-traced: the position settles
                            // + clears, yet an is_sold-only gate never fired → no
                            // journal). Detect settlement by ANY terminal signal —
                            // is_sold OR a terminal status OR is_expired. All are
                            // terminal, and journalCompletedContract injects is_sold
                            // + dedups by contract_id, so at most one POST per
                            // contract regardless of which signal/path wins. (A
                            // second, independent journaling path lives in
                            // native-trader.tsx as a portfolio autorun.)
                            const settled =
                                c &&
                                (c.is_sold ||
                                    c.status === 'won' ||
                                    c.status === 'lost' ||
                                    c.status === 'sold' ||
                                    c.is_expired);
                            if (
                                settled &&
                                typeof window !== 'undefined' &&
                                typeof window.__FX_JOURNAL_MANUAL__ === 'function'
                            ) {
                                window.__FX_JOURNAL_MANUAL__(c);
                            }
                            // PR-POC.2: this after-buy journaling sub is one-shot —
                            // tear it down on settlement so its per-contract fan-out
                            // entry doesn't linger in _new_ws_poc_subscriptions for the
                            // life of the socket (one stranded entry per manual buy).
                            if (settled) {
                                try { _journal_poc_sub?.unsubscribe?.(); } catch { /* ignore */ }
                            }
                        } catch {
                            /* non-fatal */
                        }
                    }, 'internal-journal');
                }
            } catch {
                /* never let position-tracking break the buy result */
            }

            return response;
        } catch (err) {
            if (err && typeof err.code === 'string' && typeof err.message === 'string') {
                return { error: err };
            }
            return { error: { message: `buy: send failed: ${err?.message ?? String(err)}` } };
        }
    };

    // PR-B.2.6: proposal_open_contract streaming subscription. Schema is
    // byte-identical to legacy: request `{proposal_open_contract: 1,
    // subscribe: 1, contract_id?: <id>}` returns a stream of
    // `{proposal_open_contract: {...35 fields}, msg_type, subscription: {id}}`
    // messages (probe-verified 2026-05-12: bid_price, profit, status etc.
    // all live-stream tick-by-tick until contract settles or is sold).
    //
    // PR-POC.2: SHARED proposal_open_contract stream with a consumer-keyed
    // fan-out, mirroring the transaction refactor (PR-TXN.2). POC has the same
    // dual-consumer topology as transaction: the bot (api-base sends
    // {proposal_open_contract:1,subscribe:1} → contract_id=null → the `__all__`
    // stream) AND the trader's portfolio-store both subscribe `__all__`, and the
    // bot/portfolio/replay can subscribe the SAME contract_id. The old model kept
    // ONE subscription per POC key with second-subscribe-cancels-first dedup, so a
    // consumer's SUBSCRIBE silently cancelled a sibling's live stream — e.g. the
    // trader opening positions killed a running bot's `__all__` POC, so the bot
    // never saw its contracts settle ("runs forever").
    //
    // Now: ONE wire subscription per POC key (`__all__` or a contract_id), fanned
    // out to every consumer registered under that key by a STABLE consumer id
    // ('bot', 'portfolio', 'replay', …). A re-subscribe with the same (pocKey,
    // consumerKey) REPLACES that consumer's callback (no accumulation); the wire
    // sub opens lazily on the first consumer of a key and tears down only when the
    // last consumer of that key leaves — so one consumer's teardown OR subscribe
    // never starves another.
    //
    // Map: pocKey -> { sub, starting, callbacks: Map<consumerKey, cb> }.
    const _new_ws_poc_subscriptions = new Map();
    const POC_ALL_KEY = '__all__';
    let _new_ws_poc_consumer_seq = 0;

    const _fanoutPoc = (entry, envelope) => {
        for (const cb of entry.callbacks.values()) {
            try {
                cb(envelope);
            } catch {
                /* one consumer throwing must not starve the others */
            }
        }
    };

    const _ensurePocStream = async (pocKey, entry, contract_id) => {
        if (entry.sub || entry.starting) return;
        entry.starting = true;
        let socket;
        try {
            socket = await getAuthSocket();
        } catch (err) {
            entry.starting = false;
            _fanoutPoc(entry, {
                error: {
                    message: `subscribeProposalOpenContract: auth socket open failed: ${err?.message ?? String(err)}`,
                },
                echo_req: { proposal_open_contract: 1, ...(contract_id != null && { contract_id }) },
            });
            return;
        }
        // The entry may have been fully unsubscribed (or replaced) during the await.
        if (entry.callbacks.size === 0 || _new_ws_poc_subscriptions.get(pocKey) !== entry) {
            entry.starting = false;
            return;
        }
        const request = { proposal_open_contract: 1, ...(contract_id != null && { contract_id }) };
        entry.sub = socket.subscribe(request, msg => {
            if (msg?.error) {
                // A reconnect re-keys the wire sub (fresh req_id), so a re-open of the
                // same key can collide server-side. For the PERSISTENT `__all__`
                // stream that's benign (the stream is already live) — swallow it. For
                // a PER-CONTRACT key, forward it: consumers rely on the error to run
                // their fallback (portfolio sell-recovery → populateResultDetailsFrom-
                // Transaction + unsubscribe; replay → surface the error) — swallowing
                // there would strand a spinner and leak the one-shot handle.
                if (msg.error.code === 'AlreadySubscribed' && pocKey === POC_ALL_KEY) return;
                _fanoutPoc(entry, { error: msg.error, echo_req: request });
                return;
            }
            // Pass through unchanged — proposal_open_contract envelope matches legacy
            // exactly. Consumer reads `response.proposal_open_contract`.
            _fanoutPoc(entry, msg);
        });
        entry.starting = false;
    };

    // contract_id: null → the shared `__all__` stream; else that contract's stream.
    // consumer_key: stable id so a re-subscribe replaces this consumer's entry.
    // Returns a per-consumer { unsubscribe }.
    const newWsSubscribeProposalOpenContract = (contract_id, callback, consumer_key) => {
        if (typeof callback !== 'function') return { unsubscribe: () => undefined };
        const pocKey = contract_id == null ? POC_ALL_KEY : String(contract_id);
        if (!consumer_key) {
            // eslint-disable-next-line no-console
            console.warn(
                '[poc] subscribeProposalOpenContract called without a stable consumer key; keyless calls ' +
                    'accumulate fan-out callbacks (double-processing risk). Pass a key (e.g. "portfolio", "bot").'
            );
        }
        const ckey = consumer_key ?? `__anon_${(_new_ws_poc_consumer_seq += 1)}`;

        let entry = _new_ws_poc_subscriptions.get(pocKey);
        if (!entry) {
            entry = { sub: null, starting: false, callbacks: new Map() };
            _new_ws_poc_subscriptions.set(pocKey, entry);
        }
        entry.callbacks.set(ckey, callback);
        _ensurePocStream(pocKey, entry, contract_id);

        return {
            unsubscribe: () => {
                if (entry.callbacks.get(ckey) === callback) {
                    entry.callbacks.delete(ckey);
                }
                if (entry.callbacks.size === 0) {
                    try { entry.sub?.unsubscribe?.(); } catch { /* ignore */ }
                    entry.sub = null;
                    if (_new_ws_poc_subscriptions.get(pocKey) === entry) {
                        _new_ws_poc_subscriptions.delete(pocKey);
                    }
                }
            },
        };
    };

    // Full teardown (drops ALL POC consumers on ALL keys). Kept for the socket_base
    // `forgetAll('proposal_open_contract')` contract; consumers should prefer the
    // per-consumer unsubscribe handle so they don't starve siblings (e.g. a bot).
    const newWsForgetAllProposalOpenContract = () => {
        for (const [, entry] of _new_ws_poc_subscriptions) {
            entry.callbacks.clear();
            try { entry.sub?.unsubscribe?.(); } catch { /* ignore */ }
        }
        _new_ws_poc_subscriptions.clear();
    };

    // PR-B.2.7: transaction stream subscription. Schema byte-identical to
    // legacy (probe-verified): `{transaction: 1, subscribe: 1}` returns a
    // stream of `{transaction: {id, ...buy/sell/payout fields}, msg_type:
    // 'transaction'}` events. Single global subscription per session — no
    // contract_id filter at the request level.
    // PR-TXN.2: SHARED transaction stream. The `transaction` buy/sell/payout
    // stream has TWO in-app consumers on the same getAuthSocket() singleton — the
    // trader's portfolio-store (open positions) and the bot (via the
    // PublicWsApiShim). PR-TXN.1 made it a singleton where a second consumer's
    // subscribe killed the first's (active-surface-wins). Now ONE underlying
    // `{transaction:1}` subscription fans out to every registered consumer, keyed
    // by a stable consumer id so a re-subscribe REPLACES that consumer's callback
    // (no accumulation → no double-processing of buys/sells). The wire
    // subscription opens lazily on the first consumer and tears down only when the
    // last consumer leaves. No `forget_all` prewarm / duplicate subscribe → no
    // AlreadySubscribed (the guard below stays only for a reconnect-replay edge).
    const _new_ws_transaction_callbacks = new Map(); // consumer key -> callback
    let _new_ws_transaction_sub = null; // the single underlying socket.subscribe handle
    let _new_ws_transaction_key_seq = 0;
    let _new_ws_transaction_starting = false;

    const _fanoutTransaction = envelope => {
        for (const cb of _new_ws_transaction_callbacks.values()) {
            try {
                cb(envelope);
            } catch {
                /* one consumer throwing must not starve the others */
            }
        }
    };

    const _ensureTransactionStream = async () => {
        if (_new_ws_transaction_sub || _new_ws_transaction_starting) return;
        _new_ws_transaction_starting = true;
        let socket;
        try {
            socket = await getAuthSocket();
        } catch (err) {
            _new_ws_transaction_starting = false;
            _fanoutTransaction({
                error: { message: `subscribeTransaction: auth socket open failed: ${err?.message ?? String(err)}` },
                echo_req: { transaction: 1 },
            });
            return;
        }
        // All consumers unsubscribed while the socket was opening — don't subscribe.
        if (_new_ws_transaction_callbacks.size === 0) {
            _new_ws_transaction_starting = false;
            return;
        }
        // PR-TXN.2 (R1): clear any server-side transaction subscription this
        // connection may still hold before opening ours. On reconnect,
        // NewBinarySocket.replaySubscriptions() re-keys the wire sub to a FRESH
        // req_id, which orphans our stored `_new_ws_transaction_sub` handle (its
        // `unsubscribe()` targets the old req_id → becomes a no-op). Without this,
        // a later last-consumer teardown can't actually `forget` server-side, so a
        // subsequent re-open collides with the orphan (server `AlreadySubscribed`),
        // the new subscription never activates, and consumers are silently starved
        // of frames. A best-effort forget_all before subscribing guarantees a clean
        // bind; the AlreadySubscribed guard below stays only as a backstop.
        try {
            await socket.send({ forget_all: 'transaction' });
        } catch {
            /* forget_all unsupported/failed — the AlreadySubscribed guard still applies */
        }
        // A consumer may have unsubscribed during the forget_all round-trip.
        if (_new_ws_transaction_callbacks.size === 0) {
            _new_ws_transaction_starting = false;
            return;
        }
        _new_ws_transaction_sub = socket.subscribe({ transaction: 1 }, msg => {
            if (msg?.error) {
                // `AlreadySubscribed` is benign (single wire sub; only reachable via
                // a reconnect-replay race) — the stream is already live. Swallow it;
                // forward only real errors to every consumer.
                if (msg.error.code === 'AlreadySubscribed') return;
                _fanoutTransaction({ error: msg.error, echo_req: { transaction: 1 } });
                return;
            }
            _fanoutTransaction(msg);
        });
        _new_ws_transaction_starting = false;
    };

    // callback: the consumer's handler. key: a stable consumer id ('portfolio',
    // 'bot', …) so a re-subscribe replaces that consumer's entry rather than
    // adding a duplicate. Returns a per-consumer { unsubscribe }.
    const newWsSubscribeTransaction = (callback, key) => {
        if (typeof callback !== 'function') return { unsubscribe: () => undefined };
        if (!key) {
            // eslint-disable-next-line no-console
            console.warn(
                '[transaction] subscribeTransaction called without a stable consumer key; each keyless ' +
                    'call adds a separate fan-out callback (double-processing risk). Pass a stable key ' +
                    '(e.g. "portfolio", "bot").'
            );
        }
        const consumer_key = key ?? `__anon_${(_new_ws_transaction_key_seq += 1)}`;
        _new_ws_transaction_callbacks.set(consumer_key, callback);
        _ensureTransactionStream();
        return {
            unsubscribe: () => {
                if (_new_ws_transaction_callbacks.get(consumer_key) === callback) {
                    _new_ws_transaction_callbacks.delete(consumer_key);
                }
                if (_new_ws_transaction_callbacks.size === 0 && _new_ws_transaction_sub) {
                    try { _new_ws_transaction_sub.unsubscribe(); } catch { /* ignore */ }
                    _new_ws_transaction_sub = null;
                }
            },
        };
    };

    // Full teardown (drops ALL consumers). Kept for the socket_base
    // `forgetAll('transaction')` contract; consumers should prefer the
    // per-consumer unsubscribe handle so they don't starve siblings.
    const newWsForgetAllTransaction = () => {
        _new_ws_transaction_callbacks.clear();
        if (_new_ws_transaction_sub) {
            try { _new_ws_transaction_sub.unsubscribe(); } catch { /* ignore */ }
            _new_ws_transaction_sub = null;
        }
    };

    // PR-B.2.7: statement one-shot fetch. Returns the same envelope as
    // legacy: `{statement: {count, transactions: [...]}, msg_type:
    // 'statement'}`. Accepts standard pagination + filter params.
    const newWsStatement = async ({ limit, offset, description, ...filters } = {}) => {
        let socket;
        try {
            socket = await getAuthSocket();
        } catch (err) {
            return { error: { message: `statement: auth socket open failed: ${err?.message ?? String(err)}` } };
        }
        try {
            const request = { statement: 1, description: description ?? 1, ...(limit != null && { limit }), ...(offset != null && { offset }), ...filters };
            const response = await socket.send(request);
            if (response?.error) return { error: response.error };
            return response;
        } catch (err) {
            if (err && typeof err.code === 'string' && typeof err.message === 'string') {
                return { error: err };
            }
            return { error: { message: `statement: send failed: ${err?.message ?? String(err)}` } };
        }
    };

    const newWsSell = async (contract_id, bid_price) => {
        let socket;
        try {
            socket = await getAuthSocket();
        } catch (err) {
            return { error: { message: `sell: auth socket open failed: ${err?.message ?? String(err)}` } };
        }
        try {
            const response = await socket.send({ sell: contract_id, price: bid_price });
            if (response?.error) return { error: response.error };
            return response;
        } catch (err) {
            if (err && typeof err.code === 'string' && typeof err.message === 'string') {
                return { error: err };
            }
            return { error: { message: `sell: send failed: ${err?.message ?? String(err)}` } };
        }
    };

    // ------------------------------------------------------------------
    // PR-A.1.3 — PUBLIC-DATA router for the standalone '/dtrader' route.
    //
    // The @deriv/trader package (relocated to '/dtrader') fetches its market
    // dropdown, chart candles, tick stream, trade-type list and market hours
    // through the `WS` facade: WS.activeSymbols / WS.contractsFor /
    // WS.subscribeTicksHistory / WS.getTicksHistory / WS.tradingTimes /
    // WS.time / WS.storage.* / WS.wait. On new portal app_ids the legacy
    // `deriv_api` socket is gated off, so those WS methods previously hit a
    // dead path and the trade form stayed blank. (The bot at '/' avoids this
    // because it builds its own PublicWsApiShim — but the bot doesn't mount at
    // '/dtrader'.)
    //
    // These impls run on the SAME `getPublicSocket()` singleton the bot's shim
    // uses (no extra connection), and reuse the exact same response
    // translation (`translateActiveSymbolsResponse` + a contracts_for barrier
    // remap mirrored from public-ws-shim.js) so the trader sees byte-identical
    // legacy-shaped envelopes. active_symbols / contracts_for / ticks /
    // trading_times / time are all PUBLIC (no auth) on the new portal —
    // probe-confirmed in PR-A.1.10. They are registered into socket_base via
    // setPublicDataNewWsImpl below.
    // ------------------------------------------------------------------

    // Lazily obtain the public socket singleton + ensure it's connecting.
    // connect() is idempotent (NewBinarySocket guards `if (this.ws) return`).
    const getConnectedPublicSocket = () => {
        const socket = getPublicSocket();
        try {
            socket.connect();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[public-data] socket.connect() failed:', err?.message ?? String(err));
        }
        return socket;
    };

    // ------------------------------------------------------------------
    // PR-A.1.5 — readiness gate for the public-data router.
    //
    // BUG (PR #279): the impls below called `getConnectedPublicSocket()`
    // (which calls `socket.connect()`) and then IMMEDIATELY issued
    // `socket.send(request)` / `socket.subscribe(...)` without awaiting the
    // socket to be OPEN. The @deriv/trader bootstrap at '/dtrader' fires a
    // burst of ~90 public-data requests into the still-CONNECTING socket.
    // Each `socket.send()` registers a pending promise inside NewBinarySocket
    // (new-binary-socket.ts:218-225) and queues the wire frame in `send_queue`.
    // If that first connection attempt closes before opening (a transient blip
    // on the new endpoint, or the server dropping the connection under the
    // burst), `NewBinarySocket.handleClose()` rejects EVERY pending promise at
    // once with `new Error('reconnected before response')`
    // (new-binary-socket.ts:171). At least one of those ~90 rejections is
    // unhandled at the trader's call site → it bubbles to the React error
    // boundary → the whole trader unmounts ("Sorry for the interruption").
    //
    // Additionally, `getConnectedPublicSocket()` was invoked once per data
    // call (×90), so `connect()` raced repeatedly during the burst. connect()
    // is internally guarded (`if (this.ws) return`) so it usually no-ops, but
    // there is a window after handleClose() nulls `this.ws` (and before the
    // scheduled reconnect fires) where a fresh connect() could churn.
    //
    // FIX: a single shared readiness gate. `ensurePublicSocketReady()`:
    //   1. obtains the singleton + connects ONCE,
    //   2. probes liveness with a tiny one-shot `{ time: 1 }` send — which
    //      QUEUES until the socket is open and resolves only once the server
    //      has actually replied, i.e. the socket is provably OPEN,
    //   3. retries the probe (up to PUBLIC_READY_MAX_PROBE_ATTEMPTS) when it
    //      rejects with the transient "reconnected before response", waiting
    //      for the socket to reopen between attempts,
    //   4. caches the resulting promise so the entire ~90-call burst awaits the
    //      SAME gate (dedupe + serialize — the socket connects once, the burst
    //      is held behind one open signal instead of racing).
    // All public-data impls await this gate before issuing their real request.
    //
    // The bot at '/' does NOT use this path (it builds its own PublicWsApiShim
    // which calls `this.socket.send/subscribe` directly and wraps every send in
    // a `.catch`), so this gate is purely additive for the trader.
    // ------------------------------------------------------------------
    const RECONNECT_BEFORE_RESPONSE = 'reconnected before response';
    const PUBLIC_READY_MAX_PROBE_ATTEMPTS = 4;
    const PUBLIC_READY_REOPEN_DELAY_MS = 400;

    const isReconnectReject = err => {
        const msg = err?.message ?? (typeof err === 'string' ? err : '');
        return typeof msg === 'string' && msg.indexOf(RECONNECT_BEFORE_RESPONSE) !== -1;
    };

    const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

    let _public_ready_promise = null;

    // PR-A.1.9 — drive the footer/network "online" status for new portal app_ids.
    //
    // BLANK TRADE-PARAMS ROOT CAUSE: the same-origin @deriv/trader gates its
    // ENTIRE trade-params panel (stake / duration / payout / Buy) behind
    //   is_trade_enabled && form_components.length > 0 &&
    //   network_status.class === 'online'
    // (trade.tsx:226-228 → screen-large.tsx renders <TradeParamsLoader> skeleton
    // until that prop is true). On new portal app_ids the LEGACY socket is gated
    // off in socket_base.js `openNewConnection` (early `return` before
    // `config.wsEvent('open')` / `wsEvent('message')` ever fire — socket_base.js
    // ~line 87/103/129), and the network-monitor only ever sets the status to
    // 'blinker' (network_monitor_base.js `setNetworkStatus('blinking')`). So
    // `network_status.class` is PERMANENTLY 'blinker', the gate is false forever,
    // and the panel never leaves the skeleton/blank state — even though the new
    // public WS is up, contracts build, and proposals correlate (post-#285).
    //
    // FIX (additive, data-layer): once the new public WS has PROVABLY opened
    // (the readiness probe below resolves), push the canonical 'online' network
    // status into `common_store` exactly as the legacy `wsEvent('open')` →
    // network-monitor → `setNetworkStatus(status_config.online, is_online)` path
    // would have. We mirror the network_monitor_base `status_config.online`
    // shape ({ class:'online', tooltip:'Online' }). One-shot (guarded by
    // `_new_ws_network_online_marked`) so we never thrash the observable.
    //
    // SAFETY:
    //   • Gated on `useNewWs()` AND `isLegacyApiAppId()` === false — legacy
    //     app_ids keep their existing `wsEvent`-driven status untouched.
    //   • Only ever sets status to 'online' (never offline/blinker), so it
    //     cannot mask a genuine browser-offline transition — the
    //     `window 'offline'` listener + `is_network_online=false` still flow
    //     through `setNetworkStatus` independently.
    //   • Does NOT touch the bot path: the bot at '/' reads its own connection
    //     health via its PublicWsApiShim; `network_status.class` only gates the
    //     trader's FormLayout. Marking it online is harmless for the bot UI
    //     (the footer simply shows the truthful "Online" once data is flowing).
    let _new_ws_network_online_marked = false;
    const markNewWsNetworkOnline = () => {
        if (_new_ws_network_online_marked) return;
        // Only synthesize the status for the new portal; legacy app_ids get it
        // from the real legacy `wsEvent('open')`/`('message')` path.
        if (!useNewWs() || isLegacyApiAppId()) return;
        if (!common_store || typeof common_store.setNetworkStatus !== 'function') return;
        _new_ws_network_online_marked = true;
        try {
            // Mirror network_monitor_base.js `status_config.online`. `is_online`
            // reflects the real browser connectivity (navigator.onLine) so the
            // store's `is_network_online` stays accurate.
            const is_browser_online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
            common_store.setNetworkStatus(
                { class: 'online', tooltip: localize('Online') },
                is_browser_online
            );
        } catch (err) {
            // Never let a status push break the readiness gate.
            _new_ws_network_online_marked = false;
            // eslint-disable-next-line no-console
            console.warn('[public-data] markNewWsNetworkOnline failed:', err?.message ?? String(err));
        }
    };

    // Resolve once the public socket has answered a one-shot probe (proof of
    // OPEN). Cached; transient reconnect-rejects are retried. On exhaustion the
    // gate resolves anyway (never rejects) so callers fall through to their own
    // request + defensive catch rather than hanging or surfacing an unhandled
    // rejection.
    const ensurePublicSocketReady = () => {
        if (_public_ready_promise) return _public_ready_promise;
        _public_ready_promise = (async () => {
            const socket = getConnectedPublicSocket();
            for (let attempt = 0; attempt < PUBLIC_READY_MAX_PROBE_ATTEMPTS; attempt++) {
                try {
                    // `send` queues until OPEN and resolves on the server reply,
                    // so a successful resolve proves the socket is open.
                    await socket.send({ time: 1 });
                    // Provably OPEN → flip the trader's network gate to 'online'.
                    markNewWsNetworkOnline();
                    return socket;
                } catch (err) {
                    if (isReconnectReject(err) && attempt < PUBLIC_READY_MAX_PROBE_ATTEMPTS - 1) {
                        // Mid-connect close — NewBinarySocket schedules its own
                        // reconnect. Wait for it to reopen, then re-probe.
                        // eslint-disable-next-line no-await-in-loop
                        await delay(PUBLIC_READY_REOPEN_DELAY_MS);
                        try {
                            socket.connect();
                        } catch {
                            /* idempotent — ignore */
                        }
                        continue;
                    }
                    // A real error response to `time` (e.g. RateLimit) still
                    // proves the socket is OPEN and round-tripping — good enough
                    // for a readiness gate. Stop probing (and mark online — the
                    // socket is demonstrably round-tripping frames).
                    if (!isReconnectReject(err)) {
                        markNewWsNetworkOnline();
                        return socket;
                    }
                }
            }
            // Probe attempts exhausted while still reconnecting. Return the
            // socket anyway; the real request + its defensive catch handle the
            // residual failure without crashing the trader.
            return socket;
        })();
        return _public_ready_promise;
    };

    // Drop the cached gate so the next public-data call re-probes for OPEN.
    // Called when a real request rejects with the transient reconnect error.
    const resetPublicReadyGate = () => {
        _public_ready_promise = null;
    };

    // contracts_for barrier_category / contract_category remap — mirrors
    // public-ws-shim.js's translateContractsForResponse. The new API omits
    // `barrier_category` + `start_type` (which the trader's contract-type
    // builder keys off) and buckets HIGHER/LOWER under their own
    // contract_category instead of legacy 'callput'.
    const CONTRACT_TYPE_TO_BARRIER_CATEGORY = {
        CALL: 'euro_atm', PUT: 'euro_atm',
        CALLE: 'euro_atm', PUTE: 'euro_atm',
        HIGHER: 'euro_non_atm', LOWER: 'euro_non_atm',
        EXPIRYRANGE: 'euro_non_atm', EXPIRYMISS: 'euro_non_atm',
        EXPIRYRANGEE: 'euro_non_atm', EXPIRYMISSE: 'euro_non_atm',
        CALLSPREAD: 'euro_non_atm', PUTSPREAD: 'euro_non_atm',
        ONETOUCH: 'american', NOTOUCH: 'american',
        RANGE: 'american', UPORDOWN: 'american',
        TICKHIGH: 'american', TICKLOW: 'american',
        RUNHIGH: 'american', RUNLOW: 'american',
        MULTUP: 'american', MULTDOWN: 'american',
        DIGITMATCH: 'non_financial', DIGITDIFF: 'non_financial',
        DIGITOVER: 'non_financial', DIGITUNDER: 'non_financial',
        DIGITEVEN: 'non_financial', DIGITODD: 'non_financial',
        ASIANU: 'asian', ASIAND: 'asian',
        RESETCALL: 'reset', RESETPUT: 'reset',
    };
    const CONTRACT_TYPE_TO_LEGACY_CATEGORY = { HIGHER: 'callput', LOWER: 'callput' };

    // PR-A.1.7: per-contract-type default barrier COUNT (numeric).
    //
    // The same-origin @deriv/trader's `buildContractTypesConfig`
    // (contract-type.ts) maps each available[] item to a trade-type config by
    // matching BOTH `contract.contract_type` AND a STRICT numeric equality on
    // the barrier count:
    //     Number(contract_types[key].barrier_count) === contract.barriers
    // (e.g. Rise/Fall config has barrier_count 0, Higher/Low has 1, Ends
    // In/Out has 2). The matcher is what distinguishes ATM Rise/Fall (CALL,
    // barriers 0) from barriered Higher/Lower (CALL, barriers 1) — they share
    // the SAME contract_type, so the barrier count is the ONLY discriminator.
    //
    // Two failure modes the new options WS can hit here, both leaving the whole
    // trade panel as skeleton placeholders (no contract-type buttons → no
    // proposal sent → payout stuck at 0.00):
    //   1. `barriers` arrives as a STRING ("0"/"1") not a number → the strict
    //      `=== contract.barriers` fails for EVERY item (0 !== "0") and the
    //      matcher returns no type for any contract → empty panel.
    //   2. `barriers` is OMITTED for ATM contracts → `Number(0) === undefined`
    //      is false → CALL/PUT never map to Rise/Fall.
    // The bot path tolerates this (it does `contract.barriers === 1` only for
    // barrier-input rendering, with safe fallbacks), which is why the bot's
    // dropdowns work but the trader's panel does not.
    //
    // Fix below: COERCE `barriers` to a Number when present (handles mode 1),
    // and DERIVE it from contract_type when absent (handles mode 2). The map
    // mirrors getContractTypesConfig()'s barrier_count values for the standard
    // (in-scope) contract types; any unmapped type falls back to 0, matching
    // the legacy ATM default. A server-provided numeric `barriers` ALWAYS wins
    // (we only coerce/derive — never override a real numeric value).
    const CONTRACT_TYPE_TO_BARRIER_COUNT = {
        // ATM up/down — no barrier
        CALL: 0, PUT: 0,
        CALLE: 0, PUTE: 0,
        // Single-barrier up/down + touch/no-touch + ticks + digits + reset
        HIGHER: 1, LOWER: 1,
        ONETOUCH: 1, NOTOUCH: 1,
        TICKHIGH: 1, TICKLOW: 1,
        DIGITMATCH: 1, DIGITDIFF: 1,
        DIGITOVER: 1, DIGITUNDER: 1,
        RESETCALL: 1, RESETPUT: 1,
        CALLSPREAD: 1, PUTSPREAD: 1,
        // Double-barrier ends/stays in-out
        EXPIRYRANGE: 2, EXPIRYMISS: 2,
        EXPIRYRANGEE: 2, EXPIRYMISSE: 2,
        RANGE: 2, UPORDOWN: 2,
        // Accumulator
        ACCU: 2, ACCUMULATOR: 2,
        // No-barrier types (digits even/odd, asians, runs)
        DIGITEVEN: 0, DIGITODD: 0,
        ASIANU: 0, ASIAND: 0,
        RUNHIGH: 0, RUNLOW: 0,
    };

    // PR-A.1.7: coerce a possibly-stringy/absent `barriers` to the numeric
    // count the trader's matcher requires. Order of precedence:
    //   1. a finite numeric value the server already sent (coerce string→num),
    //   2. else the per-contract-type default from the map above,
    //   3. else 0 (the safe ATM default).
    const normalizeBarrierCount = c => {
        const raw = c?.barriers;
        if (raw !== undefined && raw !== null && raw !== '') {
            const n = Number(raw);
            if (Number.isFinite(n)) return n;
        }
        const mapped = CONTRACT_TYPE_TO_BARRIER_COUNT[c?.contract_type];
        return typeof mapped === 'number' ? mapped : 0;
    };

    // Per-contract human display names. The same-origin @deriv/trader reads
    // `contract.contract_display` off each contracts_for.available[] item
    // (buildTradeTypesConfig) and other display helpers may localize it; the
    // new WS omits this field, so without a fallback the trader stores/reads
    // `undefined` and can crash i18next's translate (`.trim()` of undefined).
    // PR-A.1.6: fill it. Server-provided `contract_display` always wins; this
    // map (and the humanizer fallback below) only fills the gap.
    const CONTRACT_TYPE_DISPLAY = {
        CALL: 'Higher', PUT: 'Lower',
        CALLE: 'Higher', PUTE: 'Lower',
        HIGHER: 'Higher', LOWER: 'Lower',
        EXPIRYRANGE: 'Ends Between', EXPIRYMISS: 'Ends Outside',
        EXPIRYRANGEE: 'Ends Between', EXPIRYMISSE: 'Ends Outside',
        CALLSPREAD: 'Call Spread', PUTSPREAD: 'Put Spread',
        ONETOUCH: 'Touch', NOTOUCH: 'No Touch',
        RANGE: 'Stays Between', UPORDOWN: 'Goes Outside',
        TICKHIGH: 'High Tick', TICKLOW: 'Low Tick',
        RUNHIGH: 'Only Ups', RUNLOW: 'Only Downs',
        MULTUP: 'Up', MULTDOWN: 'Down',
        DIGITMATCH: 'Matches', DIGITDIFF: 'Differs',
        DIGITOVER: 'Over', DIGITUNDER: 'Under',
        DIGITEVEN: 'Even', DIGITODD: 'Odd',
        ASIANU: 'Asian Up', ASIAND: 'Asian Down',
        RESETCALL: 'Reset Call', RESETPUT: 'Reset Put',
        ACCU: 'Accumulator', ACCUMULATOR: 'Accumulator',
        TURBOSLONG: 'Long', TURBOSSHORT: 'Short',
        VANILLALONGCALL: 'Call', VANILLALONGPUT: 'Put',
        LBFLOATCALL: 'Close-to-Low', LBFLOATPUT: 'High-to-Close', LBHIGHLOW: 'High-to-Low',
    };

    // Title-case a CONTRACT_TYPE code (e.g. "DIGITMATCH" → "Digitmatch") as a
    // last-resort, never-undefined fallback display string.
    const humanizeContractType = type =>
        typeof type === 'string' && type
            ? type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
            : '';

    const translatePublicContractsFor = msg => {
        const available = msg?.contracts_for?.available;
        if (!Array.isArray(available)) return msg;
        const translated = available.map(c => {
            const out = { ...c };
            if (!out.barrier_category && CONTRACT_TYPE_TO_BARRIER_CATEGORY[out.contract_type]) {
                out.barrier_category = CONTRACT_TYPE_TO_BARRIER_CATEGORY[out.contract_type];
            }
            const legacy_category = CONTRACT_TYPE_TO_LEGACY_CATEGORY[out.contract_type];
            if (legacy_category) out.contract_category = legacy_category;
            if (!out.start_type) out.start_type = 'spot';
            // PR-A.1.7: GUARANTEE a numeric `barriers` count. This is the single
            // most load-bearing field for the trader's contract-type matcher:
            // without a number here the entire trade panel renders as skeleton
            // placeholders (see normalizeBarrierCount docblock). Coerces a
            // stringy value and derives an absent one from contract_type; a real
            // numeric value the server sent is preserved verbatim.
            out.barriers = normalizeBarrierCount(out);
            // PR-A.1.7: the trader's buildDurationConfig (shared/helpers/duration.ts)
            // calls getDurationFromString(contract.min/max_contract_duration),
            // which does `String(undefined).match(...)` → NaN duration → an empty
            // units_display → no duration selector. These fields are normally
            // present on the new WS (the bot reads them raw), but fill a safe
            // default ONLY if the server truly omitted them so a missing field
            // can never blank the whole panel. expiry_type drives the same
            // pipeline's tick/intraday/daily branch.
            if (out.min_contract_duration == null || out.min_contract_duration === '') {
                out.min_contract_duration = out.expiry_type === 'tick' ? '5t' : '1d';
            }
            if (out.max_contract_duration == null || out.max_contract_duration === '') {
                out.max_contract_duration = out.expiry_type === 'tick' ? '10t' : '365d';
            }
            if (out.expiry_type == null || out.expiry_type === '') {
                // Infer from the duration unit suffix; default to 'daily' which
                // is the legacy ATM norm for financial underlyings.
                out.expiry_type = /t$/.test(String(out.min_contract_duration)) ? 'tick' : 'daily';
            }
            // Guarantee a non-empty display string (server value wins).
            if (typeof out.contract_display !== 'string' || out.contract_display === '') {
                out.contract_display =
                    CONTRACT_TYPE_DISPLAY[out.contract_type] ?? humanizeContractType(out.contract_type);
            }
            return out;
        });
        return { ...msg, contracts_for: { ...msg.contracts_for, available: translated } };
    };

    // Synthesize echo_req if the server omitted it (contract-type.ts reads
    // `trading_times_response.echo_req.trading_times`) and apply the relevant
    // response translation per msg_type.
    const translatePublicResponse = (raw, original_request) => {
        let msg = raw;
        if (msg && msg.msg_type === 'active_symbols' && Array.isArray(msg.active_symbols)) {
            msg = translateActiveSymbolsResponse(msg);
        }
        if (msg && msg.msg_type === 'contracts_for' && msg.contracts_for?.available) {
            msg = translatePublicContractsFor(msg);
        }
        if (msg && typeof msg === 'object' && original_request && !msg.echo_req) {
            msg = { ...msg, echo_req: { ...original_request } };
        }
        return msg;
    };

    // One-shot public send → Promise<legacy-shaped envelope>.
    //
    // PR-A.1.5: gate on the socket being OPEN (ensurePublicSocketReady) before
    // issuing, and retry ONCE if the send still rejects with the transient
    // "reconnected before response" (a reconnect that landed mid-flight). This
    // prevents the request being dispatched into a connecting/reconnecting
    // socket and rejected en masse. The gate resolves (never rejects), so the
    // only rejection that can reach here is from `socket.send` itself.
    const publicSend = async request => {
        await ensurePublicSocketReady();
        const socket = getConnectedPublicSocket();
        try {
            const raw = await socket.send(request);
            // PR-A.1.9: a resolved public send is independent proof the socket is
            // OPEN. Mark network online here too (idempotent, one-shot) to cover
            // the init-order edge where the readiness gate resolved+cached BEFORE
            // common_store was wired by NetworkMonitor.init — the cached gate
            // won't re-run its body, so this is the backstop that flips the
            // trader's `network_status.class` to 'online'.
            markNewWsNetworkOnline();
            return translatePublicResponse(raw, request);
        } catch (err) {
            if (!isReconnectReject(err)) throw err;
            // Mid-flight reconnect: invalidate the readiness gate, wait for the
            // socket to reopen via a fresh probe, then retry exactly once.
            resetPublicReadyGate();
            await ensurePublicSocketReady();
            const raw = await getConnectedPublicSocket().send(request);
            return translatePublicResponse(raw, request);
        }
    };

    // Defensive wrapper for the trader's one-shot data calls. The @deriv/trader
    // call sites (active_symbols / contracts_for / trading_times / time /
    // ticks_history) do NOT all attach a `.catch`, so a raw socket rejection
    // bubbling out of these would reach the React error boundary and unmount
    // the trader (the PR #279 crash). publicSend's readiness gate + retry make
    // a reject very unlikely, but as a backstop we resolve to a benign
    // legacy-shaped `{ error }` envelope the trader's own error handling copes
    // with (it already branches on `response.error` everywhere). Never throws.
    const safePublicSend = async request => {
        try {
            return await publicSend(request);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[public-data] request failed after readiness gate + retry:', err?.message ?? String(err));
            const message =
                err && typeof err.message === 'string' ? err.message : isReconnectReject(err) ? RECONNECT_BEFORE_RESPONSE : String(err);
            const code = err && typeof err.code === 'string' ? err.code : 'PublicDataUnavailable';
            return { error: { code, message }, echo_req: { ...request } };
        }
    };

    // active_symbols('brief'|'full') — the new public WS uses
    // `active_symbols: 'brief'`. translateActiveSymbolsResponse maps
    // underlying_symbol → symbol etc. so the trader's dropdown reads the
    // legacy shape.
    const newWsActiveSymbols = mode => safePublicSend({ active_symbols: mode || 'brief' });

    // contracts_for(symbol) — one-shot; the trader builds the Trade Type list
    // from this. PAYLOAD IS MINIMAL: just `{contracts_for: symbol}`. The new
    // public WS rejects unknown params with
    // `InputValidationFailed: Properties not allowed: <name>` (PR-AT.1.1 /
    // PR-B.2.8 lesson — confirmed in contracts-for.js:204-217), so we must NOT
    // add `currency` / `product_type` the legacy WS tolerated.
    const newWsContractsFor = symbol => safePublicSend({ contracts_for: symbol });

    // trading_times(date) — market hours for the requested ISO date.
    const newWsTradingTimes = date => safePublicSend({ trading_times: date });

    // time() — server epoch.
    const newWsTime = () => safePublicSend({ time: 1 });

    // ticks_history one-shot (market-closed candle fetch).
    const newWsGetTicksHistory = request => safePublicSend(request);

    // ticks_history SUBSCRIBE (live chart). Returns a legacy-shaped
    // subscriber `{ unsubscribe() }`. The callback receives each frame
    // (history first, then ohlc/tick stream) translated to the legacy shape.
    //
    // PR-A.1.5: gate the subscribe frame on the socket being OPEN. The handle
    // is returned synchronously (the trader unsubscribes off it immediately),
    // but the real `socket.subscribe` is deferred until ensurePublicSocketReady
    // resolves, so the subscribe is never dispatched into a connecting socket.
    // An `unsubscribe()` that arrives before the gate resolves sets a flag so
    // the deferred subscribe is skipped (or torn down) once it lands.
    const newWsSubscribeTicksHistory = (request, cb) => {
        let handle = null;
        let unsubscribed = false;
        ensurePublicSocketReady().then(socket => {
            if (unsubscribed) return;
            handle = socket.subscribe(request, raw => {
                try {
                    cb(translatePublicResponse(raw, request));
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('[public-data] ticks_history subscriber threw:', err);
                }
            });
        });
        return {
            unsubscribe: () => {
                unsubscribed = true;
                try { handle?.unsubscribe(); } catch { /* best-effort */ }
            },
        };
    };

    // ticks SUBSCRIBE (plain quote stream — not used by the trader today but
    // wired for completeness / parity with subscribeTicks).
    //
    // PR-A.1.5: same readiness gate + deferred-unsubscribe guard as
    // newWsSubscribeTicksHistory.
    const newWsSubscribeTicks = (symbol, cb) => {
        let handle = null;
        let unsubscribed = false;
        ensurePublicSocketReady().then(socket => {
            if (unsubscribed) return;
            handle = socket.subscribe({ ticks: symbol, subscribe: 1 }, raw => {
                try {
                    cb(translatePublicResponse(raw, { ticks: symbol }));
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('[public-data] ticks subscriber threw:', err);
                }
            });
        });
        return {
            unsubscribe: () => {
                unsubscribed = true;
                try { handle?.unsubscribe(); } catch { /* best-effort */ }
            },
        };
    };

    // forget(subscription_id) — issue a raw forget on the public socket.
    // PR-A.1.5: gate on readiness; already swallows its own rejection.
    const newWsForget = async subscription_id => {
        try {
            await ensurePublicSocketReady();
            return await getConnectedPublicSocket().send({ forget: subscription_id });
        } catch {
            return { forget: 1 };
        }
    };

    // wait(type) bootstrap-gate resolver. The trader awaits
    // WS.wait('website_status') / wait('authorize') / wait('get_settings')
    // before issuing data calls. On the new portal there is no legacy
    // authorize/website_status handshake to await — the public socket is up
    // and the auth socket is OTP-bound (no authorize message). Resolve these
    // bootstrap waits immediately with a benign synthetic envelope so the
    // trader proceeds to the now-routed data calls. Any other (unexpected)
    // wait type also resolves to avoid hanging the trader.
    const newWsWait = (...types) => {
        const type = types[0];
        switch (type) {
            case 'website_status':
                return Promise.resolve({ msg_type: 'website_status', website_status: { site_status: 'up' } });
            case 'authorize':
                return Promise.resolve({
                    msg_type: 'authorize',
                    authorize: {
                        loginid: client_store?.loginid ?? null,
                        currency: client_store?.currency ?? '',
                        balance: client_store?.balance ?? 0,
                    },
                });
            case 'get_settings':
                return Promise.resolve({ msg_type: 'get_settings', get_settings: {} });
            default:
                return Promise.resolve({ msg_type: type });
        }
    };

    // PR-A.1.3: register the public-data impls with socket_base so the
    // trader's WS.activeSymbols / WS.contractsFor / WS.subscribeTicksHistory /
    // WS.getTicksHistory / WS.tradingTimes / WS.time / WS.storage.* / WS.wait
    // resolve on the new public WS when `useNewWs()` is true. No-op on legacy
    // numeric app_ids (registry stays null → original deriv_api path).
    if (BinarySocket && typeof BinarySocket.setPublicDataNewWsImpl === 'function') {
        BinarySocket.setPublicDataNewWsImpl({
            activeSymbols: newWsActiveSymbols,
            contractsFor: newWsContractsFor,
            tradingTimes: newWsTradingTimes,
            getTicksHistory: newWsGetTicksHistory,
            subscribeTicksHistory: newWsSubscribeTicksHistory,
            subscribeTicks: newWsSubscribeTicks,
            time: newWsTime,
            // PR-A.1.5: register the crash-proof `safePublicSend` for the
            // generic `send` slot too (WS.storage.send fallback + SmartChart's
            // requestAPI). Readiness-gated, retry-once, never throws.
            send: safePublicSend,
            forget: newWsForget,
            wait: newWsWait,
        });
    }

    // PR-B.2.4 + PR-B.2.5: register the new-WS impls with socket_base so
    // `WS.subscribeProposal / .forgetAll('proposal') / .buy / .sell` calls
    // (anywhere in the codebase, including trade-store.ts which we are
    // intentionally NOT modifying) route through this service when
    // `useNewWs()` is true. Legacy app_ids fall back to the original
    // socket_base implementation byte-identically. The setter is a no-op
    // if any function reference is missing or non-callable, so this is
    // safe to call eagerly at module load.
    if (BinarySocket && typeof BinarySocket.setProposalNewWsImpl === 'function') {
        BinarySocket.setProposalNewWsImpl({
            subscribeProposal,
            forgetAllProposals,
            buy: newWsBuy,
            sell: newWsSell,
            // PR-B.2.6: proposal_open_contract streaming subscription +
            // bulk-forget. Lets portfolio-store and contract-replay-store
            // get live position updates on new app_ids without touching
            // either consumer.
            subscribeProposalOpenContract: newWsSubscribeProposalOpenContract,
            forgetAllProposalOpenContract: newWsForgetAllProposalOpenContract,
            // PR-B.2.7: transaction stream + statement one-shot fetch.
            subscribeTransaction: newWsSubscribeTransaction,
            forgetAllTransaction: newWsForgetAllTransaction,
            statement: newWsStatement,
            // PR-CU.1: contract update / history / cancel on the auth WS
            // (byte-identical request shapes; the new options WS accepts them).
            contractUpdate: newWsContractUpdate,
            contractUpdateHistory: newWsContractUpdateHistory,
            cancelContract: newWsCancelContract,
        });
    }

    const authorizeAccount = response => {
        client_store.responseAuthorize(response);
        subscribeBalances();
        WS.storage.getSettings();
        WS.getAccountStatus();
        WS.storage.payoutCurrencies();
        client_store.setIsAuthorize(true);
        if (!client_store.is_virtual) {
            WS.getSelfExclusion();
        }
        BinarySocket.sendBuffered();
        if (/bch/i.test(response.authorize.currency) && !client_store.accounts[client_store.loginid].accepted_bch) {
            // showPopup({
            //     url        : urlFor('user/warning'),
            //     popup_id   : 'warning_popup',
            //     form_id    : '#frm_warning',
            //     content_id : '#warning_content',
            //     validations: [{ selector: '#chk_accept', validations: [['req', { hide_asterisk: true }]] }],
            //     onAccept   : () => { Client.set('accepted_bch', 1); },
            // });
        }
    };

    return {
        init,
        setBalanceActiveAccount,
        setBalanceOtherAccounts,
        authorizeAccount,
        // PR-B.2.1.1: client-store.init()'s PR-A.1.3 bypass returns before
        // reaching legacy `authorizeAccount`, so `subscribeBalances` is never
        // invoked for new portal app_ids. Expose it as `startNewWsSubscriptions`
        // so the bypass path can trigger the new-WS balance subscription
        // directly. Internally branches on `useNewWs()` — safe for both
        // legacy and new app_ids.
        startNewWsSubscriptions: subscribeBalances,
        // PR-B.2.2: bootstraps balance subscriptions for ALL accounts (not just
        // active). client-store.init's PR-A.1.3 bypass calls this alongside
        // startNewWsSubscriptions to populate setBalanceOtherAccounts and
        // synthesize obj_total_balance.
        startMultiAccountBalances: subscribeMultiAccountBalances,
        // PR-B.2.3: one-shot portfolio fetch via the new auth WS. Active
        // account only; the new WS binds one OTP to one accountId so
        // portfolio is per-loginid by construction. Returns a Promise
        // resolving to a legacy-shaped envelope.
        fetchPortfolio,
        // PR-B.2.4: streaming proposal subscription + bulk forget. Per-signature
        // dedup via internal Map keyed on contract_type/symbol/duration/etc.
        // Translates `symbol` → `underlying_symbol` at the boundary
        // (probe-confirmed: legacy values work under the new field name).
        subscribeProposal,
        forgetAllProposals,
    };
})();

export default BinarySocketGeneral;

const ResponseHandlers = (() => {
    const websiteStatus = response => {
        if (response.website_status) {
            const is_server_down = checkServerMaintenance(response.website_status);

            // If the site is down or updating, connect to WebSocket with an exponentially increasing delay on every attempt.
            // Requests excluding - website_status/authorize will be blocked during backoff
            // The delay starts off at approximately 1.024 seconds and grows exponentially, with a random factor between 0.5 and 2 to spread out the reconnection attempts.
            // The maximum delay is capped at 10 minutes (600k ms).
            if (is_server_down) {
                const reconnectionDelay =
                    Math.min(Math.pow(2, reconnectionCounter + 9), 600000) * (0.5 + Math.random() * 1.5);

                window.setTimeout(() => {
                    reconnectionCounter++;
                    BinarySocket.closeAndOpenNewConnection();
                    BinarySocket.blockRequest(is_server_down);
                }, reconnectionDelay);
                // If site is up, and there was a reconnection attempted before
            } else if (!is_server_down && reconnectionCounter > 1) {
                window.location.reload();
            }

            // @deriv/deriv-api blockRequest(true) affects all API requests except website_status
            BinarySocket.blockRequest(is_server_down);
            BinarySocket.setAvailability(response.website_status.site_status);
            client_store.setWebsiteStatus(response);
        }
    };

    const balanceActiveAccount = response => {
        if (!response.error) {
            BinarySocketGeneral.setBalanceActiveAccount(response.balance);
        }
    };

    const balanceOtherAccounts = response => {
        if (!response.error) {
            BinarySocketGeneral.setBalanceOtherAccounts(response.balance);
        }
    };

    return {
        websiteStatus,
        balanceActiveAccount,
        balanceOtherAccounts,
    };
})();
